import { ImageryLayer, WebMapServiceImageryProvider } from 'cesium';
import {getFeaturesInfoByLayer, getWFSFeatureInfoByLayer, getWmsFeatureInfoByText} from '../_util/layer';
import encryptParams from "../_util/encryptParams";
import {getResource} from "../_util/getToken";

export const getCqlFilterByGroup = group => {
    let cql_filter = '';
    if (group?.length) {
        group.map(v => {
            if (!v.hasOwnProperty('show') || v.show) {
                cql_filter = cql_filter ? `${cql_filter} or (${v.filter})` : `(${v.filter})`;
            }
        });
    }

    return cql_filter;
};

class ImageLayer extends ImageryLayer {
    constructor(options) {
        let {
            url,
            params = {},
            geoserverParams = {},
            encrypt,
            key,
            modalType,
            name,
            labelField,
            template,
            autoRefresh,
            style,
            codeField,
            show = true,
            layerControl = true,
            viewOnly,
            legend,
            map,
            group,
            zIndex,
            groupLayer,
            unclickable = false,
            cancelPopup,
            token
        } = options;

        let _params = { ...params };
        if (group?.length) {
            const _group = JSON.parse(JSON.stringify(group));
            if (!show) {
                _group.map(f => {
                    f.show = false;
                });
            } else {
                _group.map(f => {
                    f.show = f.show ?? true;
                });
            }

            let _cql_filter = getCqlFilterByGroup(_group);
            if (_cql_filter) {
                _params.cql_filter = _params.cql_filter
                    ? `${_params.cql_filter} and (${_cql_filter})`
                    : _cql_filter;
            } else {
                show = false;
            }
        }

        if (encrypt) {
            _params = encryptParams(_params, encrypt);
        }

        const imageryProvider = new WebMapServiceImageryProvider({
            url: getResource(url, {token}),
            enablePickFeatures: !(viewOnly || unclickable),
            getFeatureInfoParameters: _params,
            parameters: {
                service: 'WMS',
                format: 'image/png',
                request: 'GetMap',
                version: '1.1.1',
                transparent: true,
                ..._params,
            },
            layers: _params.typeName || _params.QUERY_LAYERS || _params.layers,
            tileWidth: 1024,
            tileHeight: 1024,
            maximumLevel: 18, // 根据需求设置最大级别
            ...geoserverParams,
        });

        super(imageryProvider, { show });

        // 接口地址
        this.url = url;

        this.style = style;

        // 加密
        this.encrypt = encrypt;

        // 图层标识
        this.key = key;

        // 弹窗类型
        this.modalType = modalType;

        // 图层名称
        this.name = name;

        // 设施类型
        this.codeField = codeField;

        // 图例
        this.legend = legend;

        // 标签字段
        this.labelField = labelField;

        // 自动刷新标识
        this.autoRefresh = autoRefresh;

        // 模版内容
        this.template = template;

        // 地图
        this.map = map;

        // 图层组
        this.groupLayer = groupLayer;

        // 过滤
        this.group = group;

        // 查询条件
        this.params = params;

        // 图层层级
        this.zIndex = zIndex;

        // 图层控制
        this.layerControl = layerControl;

        // 仅展示
        this.viewOnly = viewOnly;

        // 是否允许触发点击效果
        this.unclickable = unclickable;

        // 取消气泡
        this.cancelPopup = cancelPopup;

        // token
        this.token = token;
    }

    // 重写父类的属性访问器
    get show() {
        return super.show;
    }

    set show(value) {
        this.setVisible(value);
    }

    // 获取地图
    getMap() {
        return this.map;
    }

    // 设置图层显示隐藏
    setVisible(visible) {
        super.show = visible; // 将属性传递给父类

        const map = this.getMap && this.getMap();
        if (map) {
            map.layers.emit('visible', this, visible);
        }
    }

    // 设置过滤条件
    setGroup(group, options = {}) {
        if (group?.length) {
            const params = { ...this.params, ...options };
            let filterGroup = group.filter(item => !item.hasOwnProperty('show') || item.show);

            let cql_filter = '';
            filterGroup.map(v => {
                if (v.filter) {
                    cql_filter = cql_filter ? `${cql_filter} or ${v.filter}` : v.filter;
                }
            });

            if (cql_filter) {
                params.cql_filter = params.cql_filter
                    ? `(${params.cql_filter}) and (${cql_filter})`
                    : cql_filter;
            }

            this.group = group;
            this.updateParams(params);
            this.setVisibleByGroup();
        }
    }

    // 根据过滤条件组显示图层
    setVisibleByGroup() {
        const group = this.group;
        if (group?.length) {
            let visible = false;

            group.map(v => {
                if (!v.hasOwnProperty('show') || v.show) {
                    visible = true;
                }
            });

            this.setVisible(visible);
        }
    }

    // 设置查询条件
    updateParams(params, group) {
        let filter = getCqlFilterByGroup(group);
        let _params = { ...params };
        let cql_filter = '';
        if (filter) {
            cql_filter = params.cql_filter ? `(${params.cql_filter}) and (${filter})` : filter;
        }

        if (cql_filter) {
            _params = { ..._params, cql_filter };
        }

        if (this.encrypt) {
            _params = encryptParams(_params, this.encrypt);
        }

        const imageryProvider = this.imageryProvider;
        imageryProvider._pickFeaturesResource.setQueryParameters(_params);
        imageryProvider._resource.setQueryParameters(_params);

        // 真正参数变化需要设置 _tileProvider
        imageryProvider._tileProvider._pickFeaturesResource.setQueryParameters(_params);
        imageryProvider._tileProvider._resource.setQueryParameters(_params);

        // 触发重新请求
        imageryProvider?._reload?.();
    }

    getGroupIndex() {
        return this.groupLayer.indexOf(this);
    }

    getMapIndex(key = this.key) {
        return this.map.layers.getLayerIndex(key);
    }

    getFeatureByFieldValue(field, value) {
        return getWFSFeatureInfoByLayer(this, `${field} = '${value}'`);
    }

    // 根据条件获取图形数据，用于地图搜索
    getFeaturesBySearch(field, value) {
        return getWmsFeatureInfoByText(this, field, value);
    }

    // 根据条件展示对应的视角
    fitViewByFilter(filter, options = {}) {
        getFeaturesInfoByLayer(this, {cql_filter: filter}).then(geoJson => {
            if (geoJson?.features?.length) {
                this.map.fitView({geoJson, options: {padding: [3000, 3000], ...options}});
            }
        });
    }
}

export default ImageLayer;
